import type { CookieConsentInstance } from '@/shims-vue';
import type {
  ConsentCookie,
  ConsentPreferences,
  CookieConsentV3Preferences,
} from '@/types/cookie-consent';
import { getConsentCookie, getConsentPreferences } from './storage';

/**
 * Adapts vanilla-cookieconsent v3 getUserPreferences() result to our ConsentPreferences shape.
 */
export function adaptV3PreferencesToStorage(
  prefs: CookieConsentV3Preferences,
): ConsentPreferences {
  return {
    accept_type: prefs.acceptType,
    accepted_categories:
      prefs.acceptedCategories as ConsentPreferences['accepted_categories'],
    rejected_categories:
      prefs.rejectedCategories as ConsentPreferences['rejected_categories'],
  };
}

/**
 * Checks if analytical consent is enabled in the given consent cookie.
 * @param prefs - ConsentCookie to check.
 * @returns True if analytical consent is enabled.
 */
export function hasAnalyticalConsent(prefs: ConsentCookie | null): boolean {
  if (!prefs) {
    return false;
  }

  return (
    (prefs.level?.includes('analytical') ||
      prefs.accepted_categories?.includes('analytical')) ??
    false
  );
}

/**
 * Push / FCM are tied to the "functional" cookie category (see cookie-config).
 */
export function hasFunctionalConsent(): boolean {
  const preferences = getConsentPreferences();
  if (preferences) {
    if (preferences.accept_type === 'all') {
      return true;
    }
    return preferences.accepted_categories.includes('functional');
  }

  const cookie = getConsentCookie();
  if (!cookie) {
    return false;
  }

  return !!(
    cookie.level?.includes('functional') ||
    cookie.accepted_categories?.includes('functional')
  );
}

/**
 * Applies consent preferences to the cookie consent library (v3 API).
 * @param prefs - ConsentPreferences to apply.
 * @param $cc - Cookie consent instance (vanilla-cookieconsent v3).
 */
export function applyConsentPreferences(
  prefs: ConsentPreferences,
  $cc: CookieConsentInstance,
): void {
  if (prefs.accept_type === 'all') {
    console.log('[CookieConsent] Applying full consent');
    $cc.acceptCategory('all');
  } else if (prefs.accept_type === 'necessary') {
    $cc.acceptCategory([]);
  } else if (prefs.accepted_categories?.length) {
    $cc.acceptCategory(prefs.accepted_categories);
  }
}

/**
 * Restores consent from localStorage and applies it to the cookie consent library.
 * Also re-enables Matomo if analytical consent was granted.
 * @param $cc - Cookie consent instance.
 * @param shouldHideModal - Whether to hide the consent modal after applying.
 */
export function restoreAndApplyConsent(
  $cc: CookieConsentInstance,
  shouldHideModal = false,
): void {
  console.log('[CookieConsent] Restoring consent from storage');

  // Check and enable Matomo if analytical consent exists
  const consentCookie = getConsentCookie();
  if (hasAnalyticalConsent(consentCookie)) {
    console.log('[CookieConsent] Enabling Matomo via stored consent settings');
    window._matomoOkay = true;
  }

  // Restore and apply consent preferences
  const preferences = getConsentPreferences();
  if (preferences) {
    applyConsentPreferences(preferences, $cc);
  }

  // On iOS we skip showing the banner only when consent was already persisted (localStorage).
  // First launch has no preferences — do not hide, or users never see the dialog.
  if (shouldHideModal && preferences) {
    $cc.hide();
  }
}
